import { create, type StateCreator } from 'zustand'
import { devtools } from 'zustand/middleware'

import { CompanyPagingModel } from 'app/models/companyPagingModel'
import { UserPagingModel } from 'app/models/user/userPagingModel'
import { DatabasePagingModel } from 'app/models/databasePagingModel'
import { zustandPlugin } from '@/modules/shared/openreplay'

type State = {
  companies: CompanyPagingModel
  tenantCompanies: CompanyPagingModel
  users: UserPagingModel
  databases: DatabasePagingModel
}

type Actions = {
  setCompanyPaging: (paging: CompanyPagingModel) => void
  setTenantCompanyPaging: (paging: CompanyPagingModel) => void
  setUserPaging: (paging: UserPagingModel) => void
  setDatabasePaging: (paging: DatabasePagingModel) => void
}

type StoreMutators = [['zustand/devtools', never]]

const trackedCreator = zustandPlugin as unknown as <T>(
  creator: StateCreator<T, StoreMutators, []>,
  name?: string,
) => StateCreator<T, StoreMutators, []>

/** Wartości startowe przeniesione 1:1 z `applicationReducer.initialState`. */
const emptyCompanyPaging: CompanyPagingModel = {
  descending: true,
  orderBy: 'createdDate',
  skip: 0,
  take: 10,
  filters: {
    searchValue: '',
    companySettlementTypes: [],
    companyTypes: [],
    packageOptions: [],
    synchronizationOptions: [],
    packageProposalOptions: [],
    subscriptionExpirationDateOptions: [],
    suspendedOptions: [],
    subcompanies: false,
  },
} as CompanyPagingModel

const emptyUserPaging: UserPagingModel = {
  descending: true,
  orderBy: 'createdDate',
  skip: 0,
  take: 10,
  searchValue: '',
} as UserPagingModel

const emptyDatabasePaging: DatabasePagingModel = {
  descending: true,
  orderBy: 'createdDate',
  skip: 0,
  take: 10,
  searchValue: '',
} as DatabasePagingModel

/**
 * Stronicowanie list panelu administracyjnego.
 *
 * To jedyne, co realnie zostało ze slice'a `application` — reszta jego pól (daty,
 * rok, miesiąc, zwinięcie menu) nie miała ani jednego czytelnika, bo opierała się na
 * `extractUrlParam`, który do dziś nie dopasowywał żadnego adresu.
 *
 * Cztery ekrany zapisują tu swoje ustawienia listy i odczytują je przy powrocie na
 * zakładkę, więc stan musi przeżyć odmontowanie komponentu — dlatego store, a nie
 * stan lokalny.
 */
export const useAdminPagingStore = create<State & Actions>()(
  devtools(
    trackedCreator(
      (set) => ({
        companies: emptyCompanyPaging,
        // Osobna kopia: obie listy zmieniaja sie niezaleznie.
        tenantCompanies: { ...emptyCompanyPaging },
        users: emptyUserPaging,
        databases: emptyDatabasePaging,

        setCompanyPaging: (paging) => set({ companies: paging }, false, 'companies'),
        setTenantCompanyPaging: (paging) =>
          set({ tenantCompanies: paging }, false, 'tenantCompanies'),
        setUserPaging: (paging) => set({ users: paging }, false, 'users'),
        setDatabasePaging: (paging) => set({ databases: paging }, false, 'databases'),
      }),
      'admin-paging',
    ),
    { name: 'EasyTacho', store: 'admin-paging' },
  ),
)
